import {
    ExternalLink,
    MessageCircleIcon,
    MessageSquareTextIcon,
} from 'lucide-react';
import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import * as S from './HeaderSection.style';
import SubRouteNavigation from '../sub-route-navigation/SubRouteNavigation';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useFeature, useMatchesHandleData } from '@modules/root';
import { useRef, useState, useEffect } from 'react';
import { Link, useLoaderData } from 'react-router';
import type { ProfessionalDetailRootLoaderData } from '../../page/ProfessionalPage';
import type { HeaderSectionProps } from './HeaderSection';
import { useSize } from 'ahooks';
import { BannerAndLogo } from '@modules/professional/component/overview-tab/banner-and-logo/BannerAndLogo';
import { BannerSegmentEnum } from 'generated/graphql';
import { Button as RoccoButton } from '@rocco/components/button';
import { ChevronDown } from '@archipro-design/icons';
import { useUserLoggedIn } from '~/modules/user';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '../../util/constants';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import ActionCompletedIcon from '~/modules/tracking/component/ActionCompletedIcon';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { HDEExhibitorBadge } from '@modules/hde/components/HDEExhibitorBadge';
import { HDE_PAGE_PATH } from '@modules/hde/constants';
import { useProfessionalChat } from '~/modules/chat/hooks/use-professional-chat';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';

const ENABLE_SCROLL_INDICATOR = false;

export const HeaderSectionDesktop = ({
    openEnquiryModal,
    banners,
    canCarouselControl,
}: HeaderSectionProps) => {
    const { linkedProfiles, showHDEExhibitorMark } =
        useLoaderData<ProfessionalDetailRootLoaderData>();
    const professional = useProfessionalInitialRouteData();
    const {
        Title,
        Link: professionalLink,
        LibraryLocations,
        CompanyLogo,
        LogoBackgroundColor,
        Website,
        ID,
        Content,
        primaryCTA,
    } = professional;
    const { mobile } = useAppDisplayModeContext();
    const { isLoggedIn } = useUserLoggedIn();
    const newChatUI2025Enabled = useFeature('new_chat_ui_2025');
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { startProfessionalChat } = useProfessionalChat();

    const variant = 'default';

    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const hasLinkedLocation =
        linkedProfiles?.linkedProfilesList.some((p) =>
            p.LibraryLocations.some((l) => !l.LocationHidden),
        ) ?? false;

    const IS_CLIENT_SIDE = typeof window !== 'undefined';
    const innerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [isSticky, setIsSticky] = useState(false);
    const [isHidden, setIsHidden] = useState(!ENABLE_SCROLL_INDICATOR);
    const [chevronOpacity, setChevronOpacity] = useState(1);

    const screenSize = {
        width: IS_CLIENT_SIDE ? window.innerWidth : 0,
        height: IS_CLIENT_SIDE ? window.innerHeight : 0,
    };
    const heroSize = useSize(innerRef);

    const { css } = useStyles({
        heroSize,
        screenSize,
        isBMPreview,
        isSticky,
        variant,
    });

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(Website, {
            libraryItemID: ID,
            professionalID: ID,
            professionalTitle: Title,
            source: 'ProfessionalHeader',
        });

    const { hasVisited, markVisited } = useWebsiteVisited(ID);
    const { hasSubmitted } = useEnquirySubmitted(ID);

    const contactLabel = 'Contact';
    const primaryEnquiryLabel =
        primaryCTA?.primaryCTALabelTitleCase ?? 'Get in Touch';

    const excludeAboutBanner = banners?.filter(
        (b) => b.bannerSegment !== BannerSegmentEnum.Aboutus,
    );

    const bannerRef = useRef(null);

    useEffect(() => {
        if (!sentinelRef.current || !bannerRef.current) return;

        // Set up intersection observer for sticky header
        const observer = new IntersectionObserver(
            ([entry]) => {
                // the sentinelRef triggers the event when going out of the screen while scrolling up
                // checking intersectionRect to make sure we're in the right position
                if ((entry?.intersectionRect?.x ?? 0) > 0) {
                    setIsSticky(!entry?.isIntersecting);
                }
            },
            {
                threshold: [0.01],
                rootMargin: '0px',
            },
        );

        observer.observe(sentinelRef.current);

        // Handle scroll and resize for chevron visibility
        const handleScrollResize = () => {
            if (!ENABLE_SCROLL_INDICATOR) return;

            const projectsSection = document.querySelector(
                `#${PROFESSIONAL_SECTIONS_ANCHOR}`,
            );
            const offset = 80;

            if (projectsSection) {
                const projectsRect = projectsSection.getBoundingClientRect();
                setIsHidden(projectsRect.top < window.innerHeight + 50);
                const fadeDistance = window.innerHeight * 0.8; // Adjust percentage as needed
                const newOpacity = Math.max(
                    0,
                    (projectsRect.top - window.innerHeight + offset) /
                        fadeDistance,
                );
                if (window.scrollY < 5) {
                    setChevronOpacity(1);
                } else {
                    setChevronOpacity(newOpacity);
                }
            }
        };

        window.addEventListener('scroll', handleScrollResize);
        window.addEventListener('resize', handleScrollResize);

        handleScrollResize();

        // Cleanup function
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScrollResize);
            window.removeEventListener('resize', handleScrollResize);
        };
    }, []);

    const scrollToProjects = () => {
        const projectsSection = document.querySelector(
            `#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        );
        if (projectsSection) {
            projectsSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <>
            {excludeAboutBanner && (
                <div ref={bannerRef} className="relative z-0 w-full">
                    <BannerAndLogo
                        banners={excludeAboutBanner}
                        awards={[]}
                        professional={{
                            CompanyLogo: CompanyLogo ?? '',
                            LogoBackgroundColor: LogoBackgroundColor ?? '',
                            Title: Title ?? '',
                            Link: professionalLink ?? '',
                            Website: Website ?? '',
                            ID: ID ?? 0,
                            Content: Content ?? '',
                            bannerOverlay: professional.BannerOverlay,
                        }}
                        hideLogo={true}
                        hideCarouselArrow={!canCarouselControl}
                        variant={variant}
                    />

                    {showHDEExhibitorMark && (
                        <div className="absolute top-[10.4rem] right-[2rem] z-3 h-44">
                            <Link
                                to={HDE_PAGE_PATH}
                                aria-label="View Home Design Event"
                                className="block transition-opacity hover:opacity-90"
                            >
                                <HDEExhibitorBadge />
                            </Link>
                        </div>
                    )}

                    {!isHidden && (
                        <div
                            className="fixed bottom-10 z-fixed w-full cursor-pointer duration-500 will-change-[transform,opacity]"
                            onClick={scrollToProjects}
                            style={{ opacity: chevronOpacity }}
                        >
                            <div className="flex justify-center">
                                <ChevronDown className="animate-bounce text-white transition-opacity duration-500 ease-in-out [&_path]:fill-white" />
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div
                ref={wrapperRef}
                className={css(S.ProfessionalHeaderFixedWrapper)}
            >
                <div
                    ref={sentinelRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                    }}
                />
                <div className={css(S.ProfessionalHeaderContainer)}>
                    <div className={css(S.PageHeader)}>
                        <div ref={innerRef} className={css(S.PageHeaderInner)}>
                            <div className={css(S.LeftPane)}>
                                <SubRouteNavigation>
                                    {showEnquiryCTA &&
                                        !LibraryLocations.some(
                                            (l) => !l.LocationHidden,
                                        ) &&
                                        !hasLinkedLocation && (
                                            <RoccoButton
                                                color="secondary"
                                                onClick={() => {
                                                    openEnquiryModal({
                                                        title: contactLabel,
                                                        ctaLabel: contactLabel,
                                                    });
                                                }}
                                            >
                                                <span className="tracking-0 md:tracking-0 text-sm md:text-base">
                                                    {contactLabel}
                                                </span>
                                            </RoccoButton>
                                        )}
                                </SubRouteNavigation>
                            </div>
                            <div className={css(S.RightPane)}>
                                <div className={css(S.RightPaneInner)}>
                                    {referralTrackingURL && (
                                        <RoccoButton color="secondary" asChild>
                                            <a
                                                href={referralTrackingURL}
                                                target="_blank"
                                                {...referralTrackingParams}
                                                rel="noopener"
                                                onClick={markVisited}
                                            >
                                                Visit Website
                                                <ActionCompletedIcon
                                                    isCompleted={hasVisited}
                                                    defaultIcon={
                                                        <ExternalLink className="ml-2 size-4" />
                                                    }
                                                    className="ml-2 size-4"
                                                />
                                            </a>
                                        </RoccoButton>
                                    )}
                                    {newChatUI2025Enabled &&
                                        isLoggedIn &&
                                        !mobile && (
                                            <TextTooltip
                                                className="max-w-90 text-base"
                                                content="This is a preview of professional chats. This is only visible to certain staff members. Clients cannot see this button."
                                                delayDuration={0}
                                            >
                                                <RoccoButton
                                                    color="secondary"
                                                    onClick={() => {
                                                        startProfessionalChat(
                                                            professional,
                                                        );
                                                    }}
                                                >
                                                    Chat
                                                    <MessageSquareTextIcon className="ml-2 size-4" />
                                                </RoccoButton>
                                            </TextTooltip>
                                        )}
                                    {showEnquiryCTA && (
                                        <RoccoButton
                                            color="dark"
                                            onClick={() =>
                                                openEnquiryModal({
                                                    title: primaryEnquiryLabel,
                                                    ctaLabel:
                                                        primaryEnquiryLabel,
                                                })
                                            }
                                        >
                                            {primaryEnquiryLabel}
                                            <ActionCompletedIcon
                                                isCompleted={hasSubmitted}
                                                defaultIcon={
                                                    <MessageCircleIcon className="ml-2 size-4" />
                                                }
                                                className="ml-2 size-4"
                                            />
                                        </RoccoButton>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
